import { useEffect, useRef, useState } from "react";
import axios from "axios";
import api from "../../lib/api";
import { toast } from "sonner";
import { UploadSimple, FilePdf, FileImage, File as FileIcon, DownloadSimple, Eye, Lock } from "@phosphor-icons/react";
import { API } from "../../App";

const ALLOWED = [
  "image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
];
const MAX_PER_FILE = 5 * 1024 * 1024;
const MAX_PER_TASK = 25 * 1024 * 1024;

function fmtSize(b) {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
}

function iconFor(ctype) {
  if (!ctype) return FileIcon;
  if (ctype.startsWith("image/")) return FileImage;
  if (ctype === "application/pdf") return FilePdf;
  return FileIcon;
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function TaskAttachmentsTab({ taskId, task, user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef(null);

  const reqParams = {
    requester_user_id: user?.user_id || "",
    requester_name: user?.name || "",
    requester_role: user?.role || "",
  };
  const isLocked = !!task?.force_closed_at;

  const refresh = async () => {
    if (!taskId) return;
    setLoading(true); setError("");
    try {
      const r = await api.get(`${API}/crm/tasks/${taskId}/attachments`, { params: reqParams });
      setRows(r.data || []);
    } catch (e) {
      setError(e?.response?.status === 403 ? "No access to attachments." : "Could not load attachments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    if (isLocked) { toast.error("Task is force-closed; uploads are blocked."); return; }
    setUploading(true);
    const totalExisting = rows.reduce((s, r) => s + (r.size || 0), 0);
    let runningTotal = totalExisting;
    for (const file of Array.from(files)) {
      // Client-side validation
      if (!ALLOWED.includes(file.type)) {
        toast.error(`Unsupported file type: ${file.name}`); continue;
      }
      if (file.size > MAX_PER_FILE) {
        toast.error(`${file.name} exceeds 5MB limit`); continue;
      }
      if (runningTotal + file.size > MAX_PER_TASK) {
        toast.error(`Would exceed 25MB per-task limit. Skipping ${file.name}.`); continue;
      }
      try {
        const dataUrl = await fileToDataUrl(file);
        const r = await api.post(`${API}/crm/tasks/${taskId}/attachments`, {
          file_name: file.name,
          content_type: file.type,
          size: file.size,
          data_url: dataUrl,
          ...reqParams,
        });
        setRows((prev) => [r.data, ...prev]);
        runningTotal += file.size;
        toast.success(`Uploaded ${file.name}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const downloadAttachment = async (att) => {
    try {
      const r = await api.get(`${API}/crm/tasks/${taskId}/attachments/${att.attachment_id}`, { params: reqParams });
      const link = document.createElement("a");
      link.href = r.data.data_url;
      link.download = r.data.file_name || "file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      toast.error("Could not download");
    }
  };

  const viewAttachment = async (att) => {
    try {
      const r = await api.get(`${API}/crm/tasks/${taskId}/attachments/${att.attachment_id}`, { params: reqParams });
      const w = window.open();
      if (w) {
        w.document.write(
          `<title>${att.file_name}</title><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;height:100vh">` +
          (att.content_type?.startsWith("image/")
            ? `<img src="${r.data.data_url}" style="max-width:100%;max-height:100%"/>`
            : `<iframe src="${r.data.data_url}" style="width:100%;height:100%;border:0"></iframe>`) +
          `</body>`
        );
      }
    } catch (e) {
      toast.error("Could not open file");
    }
  };

  if (error) return <div data-testid="task-attach-error" className="text-sm text-red-600 border border-red-200 bg-red-50 p-3">{error}</div>;

  const totalUsed = rows.reduce((s, r) => s + (r.size || 0), 0);

  return (
    <div data-testid="task-attachments-tab" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-slate-500">
          Allowed: images / PDF / Word / Excel / TXT · Max 5 MB / file · 25 MB / task
          <div className="text-[10px] text-slate-400 mt-0.5">Used {fmtSize(totalUsed)} / 25 MB</div>
        </div>
        {isLocked ? (
          <div data-testid="task-attach-locked" className="inline-flex items-center gap-1 text-xs px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200">
            <Lock size={12} /> Uploads locked (force-closed)
          </div>
        ) : (
          <>
            <input
              ref={fileInput}
              type="file"
              data-testid="task-attach-input"
              multiple
              accept={ALLOWED.join(",")}
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
            />
            <button
              data-testid="task-attach-upload-btn"
              type="button"
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] disabled:bg-slate-300 px-4 py-2 text-xs uppercase tracking-wider font-bold inline-flex items-center gap-1"
            >
              <UploadSimple size={12} weight="bold" />
              {uploading ? "Uploading…" : "Upload Proof"}
            </button>
          </>
        )}
      </div>

      {loading && <div className="text-sm text-slate-400">Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-sm text-slate-400 italic" data-testid="task-attach-empty">No attachments yet.</div>}

      {rows.map((a) => {
        const Icon = iconFor(a.content_type);
        return (
          <div key={a.attachment_id} data-testid={`task-attach-row-${a.attachment_id}`} className="border border-slate-200 p-3 flex items-center gap-3 bg-white">
            <Icon size={28} className="text-slate-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate" title={a.file_name}>{a.file_name}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {fmtSize(a.size)} · uploaded by <span className="font-semibold">{a.uploaded_by_name || "—"}</span> · {fmtWhen(a.uploaded_at)}
              </div>
            </div>
            <button
              data-testid={`task-attach-view-${a.attachment_id}`}
              onClick={() => viewAttachment(a)}
              className="p-2 hover:bg-slate-100 text-slate-600" title="View"
            ><Eye size={14} /></button>
            <button
              data-testid={`task-attach-download-${a.attachment_id}`}
              onClick={() => downloadAttachment(a)}
              className="p-2 hover:bg-slate-100 text-slate-600" title="Download"
            ><DownloadSimple size={14} /></button>
          </div>
        );
      })}
    </div>
  );
}
